import { getSheetName, getSheet, getSheetIndexFromId, setCell, getTypeFromFormat, isHiddenRow, isHiddenCol } from '../index';
import { getCell, getSheetIndex, checkFormulaRef, parseFormulaArgument, sheetRenameUpdate } from '../index';
import { workbookFormulaOperation, getColumnHeaderText, aggregateComputation, clearFormulaDependentCells, formulaInValidation, applyCF, getCellRefValue, commputeFormulaValue, getAutoDetectFormatParser, calculateFormula } from '../common/index';
import { Calculate, ValueChangedArgs, CommonErrors, getAlphalabel } from '../../calculate/index';
import { isNullOrUndefined, getNumericObject } from '@syncfusion/ej2-base';
import { Deferred } from '@syncfusion/ej2-data';
import { getCellAddress, getFormattedCellObject, isNumber, checkIsFormula, removeUniquecol, removeSortcol, checkUniqueRange, checkSortRange } from '../common/index';
import { getRangeAddress, getRangeFromAddress, isCellReference, refreshInsertDelete, getUpdatedFormulaOnInsertDelete } from '../common/index';
import { getUniqueRange, getSortRange, DefineName, selectionComplete, getRangeIndexes, getSwapRange } from '../common/index';
import { updateSheetFromDataSource, importModelUpdate } from '../common/index';
import { formulaBarOperation } from '../../spreadsheet/common/event';
import { locale } from '../../spreadsheet/common/constant';
/**
 * @hidden
 * The `WorkbookFormula` module is used to handle the formula operation in Workbook.
 */
export class WorkbookFormula {
    /**
     * Constructor for formula module in Workbook.
     *
     * @param {Workbook} workbook - Specifies the workbook.
     * @private
     */
    constructor(workbook) {
        this.uniqueOBracket = String.fromCharCode(129);
        this.uniqueCBracket = String.fromCharCode(130);
        this.uniqueCSeparator = String.fromCharCode(131);
        this.uniqueCOperator = String.fromCharCode(132);
        this.uniquePOperator = String.fromCharCode(133);
        this.uniqueSOperator = String.fromCharCode(134);
        this.uniqueMOperator = String.fromCharCode(135);
        this.uniqueDOperator = String.fromCharCode(136);
        this.uniqueModOperator = String.fromCharCode(137);
        this.uniqueConcateOperator = String.fromCharCode(138);
        this.uniqueEqualOperator = String.fromCharCode(139);
        this.uniqueExpOperator = String.fromCharCode(140);
        this.uniqueGTOperator = String.fromCharCode(141);
        this.uniqueLTOperator = String.fromCharCode(142);
        this.sheetInfo = [];
        this.parent = workbook;
        this.init();
    }
    init() {
        this.addEventListener();
        this.initCalculate();
        this.registerSheet();
        this.parent.customFormulaCollection.forEach((value, key) => {
            this.addCustomFunction(value.handler, key, value.description, value.category);
        });
    }
    /**
     * To destroy the formula module.
     *
     * @returns {void}
     * @hidden
     */
    destroy() {
        this.removeEventListener();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (this.parent.refreshing) {
            this.clearAllUniqueFormulaValue();
            this.clearAllSortFormulaValue();
            const formulaCollect = this.calculateInstance.getLibraryFormulas();
            formulaCollect.forEach((value, key) => {
                if (value.isCustom) {
                    this.parent.customFormulaCollection.set(key, { handler: value.handler, description: value.description });
                }
            });
        }
        this.calculateInstance.dispose();
        this.calculateInstance = null;
        if (this.sheetInfo) {
            this.sheetInfo = [];
        }
        this.parent = null;
    }
    addEventListener() {
        this.parent.on(workbookFormulaOperation, this.performFormulaOperation, this);
        this.parent.on(aggregateComputation, this.aggregateComputation, this);
        this.parent.on(getUniqueRange, this.getUniqueRange, this);
        this.parent.on(removeUniquecol, this.removeUniquecol, this);
        this.parent.on(getSortRange, this.getSortRange, this);
        this.parent.on(removeSortcol, this.removeSortcol, this);
        this.parent.on(clearFormulaDependentCells, this.clearFormulaDependentCells, this);
        this.parent.on(formulaInValidation, this.formulaInValidation, this);
        this.parent.on(refreshInsertDelete, this.refreshInsertDelete, this);
        this.parent.on(getUpdatedFormulaOnInsertDelete, this.getUpdatedFormulaOnInsertDelete, this);
        this.parent.on(checkFormulaRef, this.autoCorrectCellRef, this);
        this.parent.on(parseFormulaArgument, this.parseFormulaArgument, this);
        this.parent.on(getCellRefValue, this.getCellRefValue, this);
        this.parent.on(commputeFormulaValue, this.commputeFormulaValue, this);
        this.parent.on(sheetRenameUpdate, this.renameUpdation, this);
        this.parent.on(importModelUpdate, this.initFormulaOnImport, this);
    }
    removeEventListener() {
        if (!this.parent.isDestroyed) {
            this.parent.off(workbookFormulaOperation, this.performFormulaOperation);
            this.parent.off(aggregateComputation, this.aggregateComputation);
            this.parent.off(getUniqueRange, this.getUniqueRange);
            this.parent.off(getSortRange, this.getSortRange);
            this.parent.off(removeUniquecol, this.removeUniquecol);
            this.parent.off(removeSortcol, this.removeSortcol);
            this.parent.off(clearFormulaDependentCells, this.clearFormulaDependentCells);
            this.parent.off(formulaInValidation, this.formulaInValidation);
            this.parent.off(refreshInsertDelete, this.refreshInsertDelete);
            this.parent.off(getUpdatedFormulaOnInsertDelete, this.getUpdatedFormulaOnInsertDelete);
            this.parent.off(checkFormulaRef, this.autoCorrectCellRef);
            this.parent.off(parseFormulaArgument, this.parseFormulaArgument);
            this.parent.off(getCellRefValue, this.getCellRefValue);
            this.parent.off(commputeFormulaValue, this.commputeFormulaValue);
            this.parent.off(sheetRenameUpdate, this.renameUpdation);
            this.parent.off(importModelUpdate, this.initFormulaOnImport);
        }
    }
    /**
     * Get the module name.
     *
     * @returns {string} - Get the module name.
     * @private
     */
    getModuleName() {
        return 'workbookFormula';
    }
    initCalculate() {
        this.calculateInstance = new Calculate(this.parent);
        this.calcID = this.calculateInstance.createSheetFamilyID();
        this.calculateInstance.setTreatEmptyStringAsZero(true);
        this.calculateInstance.grid = this.parent.getActiveSheet().id.toString();
        this.calculateInstance.setParseArgumentSeparator(this.parent.listSeparator);
        const decimalSeparator = getNumericObject(this.parent.locale).decimal;
        if (decimalSeparator !== '.' && this.parent.listSeparator !== decimalSeparator) {
            this.calculateInstance.setParseDecimalSeparator(decimalSeparator);
        }
    }
    clearFormulaDependentCells(args) {
        if (args.isOpen) {
            this.calculateInstance.getDependentCells().clear();
            this.calculateInstance.getFormulaInfoTable().clear();
            this.calculateInstance.getDependentFormulaCells().clear();
            this.calculateInstance.uniqueRange = [];
            this.calculateInstance.sortRange = [];
            this.calculateInstance.dependencyCollection = [];
            return;
        }
        let cellRef = args.cellRef.split(':')[0];
        const sheetId = this.parent.getActiveSheet().id.toString();
        const family = this.calculateInstance.getSheetFamilyItem(sheetId);
        if (family.isSheetMember && !isNullOrUndefined(family.parentObjectToToken)) {
            cellRef = family.parentObjectToToken.get(sheetId) + cellRef;
        }
        if (args.clearFormulaInfo && this.calculateInstance.getFormulaInfoTable().has(cellRef)) {
            this.calculateInstance.getFormulaInfoTable().delete(cellRef);
        }
        this.calculateInstance.clearFormulaDependentCells(cellRef);
    }
    formulaInValidation(args) {
        const col = this.calculateInstance.getLibraryFormulas().get(args.value);
        args.skip = isNullOrUndefined(col);
    }
    performFormulaOperation(args) {
        const action = args.action;
        let formulas;
        let formulaInfo;
        if (action !== 'refreshCalculate') {
            formulas = this.calculateInstance.getLibraryFormulas();
            formulaInfo = (Array.from(formulas.values()));
        }
        let collection;
        const family = this.calculateInstance.getSheetFamilyItem(args.sheetId);
        let l10n;
        switch (action) {
            case 'getLibraryFormulas':
                args.formulaCollection = Array.from(formulas.keys());
                break;
            case 'getFormulaCategory':
                collection = ['All'];
                for (let i = 1; i < Array.from(formulas.values()).length; i++) {
                    if (collection.indexOf(formulaInfo[i].category) < 0) {
                        collection.push(formulaInfo[i].category);
                    }
                }
                args.categoryCollection = collection;
                break;
            case 'dropDownSelectFormulas':
                l10n = this.parent.serviceLocator.getService(locale);
                for (let i = 0; i < Array.from(formulas.values()).length; i++) {
                    const category = (formulaInfo[i].category ?
                        (l10n.getConstant(formulaInfo[i].category.split(' ').join('').replace('&', '')) ||
                            formulaInfo[i].category) : '');
                    if (args.selectCategory === category) {
                        args.formulaCollection[i] = Array.from(formulas.keys())[i];
                    }
                }
                break;
            case 'getFormulaDescription':
                for (let i = 0; i < Array.from(formulas.values()).length; i++) {
                    if (args.selectedList === Array.from(formulas.keys())[i]) {
                        args.description = formulaInfo[i].description;
                        args.isCustom = formulaInfo[i].isCustom;
                    }
                }
                break;
            case 'registerSheet':
                this.registerSheet(args.sheetIndex, args.sheetCount);
                break;
            case 'unRegisterSheet':
                this.unRegisterSheet(args.sheetIndex, args.sheetCount, args.propertyChange, args.isNewWorkBook);
                break;
            case 'initSheetInfo':
                this.updateSheetInfo();
                break;
            case 'refreshCalculate':
                this.refreshCalculate(args);
                break;
            case 'refreshRandomFormula':
                this.refreshRandomFormula();
                this.calculateInstance.cell = '';
                break;
            case 'setArgumentSeparator':
                this.calculateInstance.setParseArgumentSeparator(this.parent.listSeparator);
                break;
            case 'addDefinedName':
                args.isAdded = this.addDefinedName(args.definedName, false, args.index, args.isEventTrigger);
                break;
            case 'removeDefinedName':
                args.isRemoved = this.removeDefinedName(args.definedName, args.scope, args.isEventTrigger);
                break;
            case 'initiateDefinedNames':
                this.initiateDefinedNames();
                break;
            case 'addSheet':
                this.sheetInfo.push({ visibleName: args.visibleName, sheet: args.sheetName, index: args.sheetId });
                break;
            case 'getSheetInfo':
                args.sheetInfo = this.sheetInfo;
                break;
            case 'deleteSheetTab':
                for (let i = 0; i < this.sheetInfo.length; i++) {
                    if (this.sheetInfo[i].index === args.sheetId) {
                        const visibleName = this.sheetInfo[i].visibleName;
                        const sheetName = this.sheetInfo[i].sheet;
                        this.sheetInfo.splice(i, 1);
                        const id = args.sheetId.toString();
                        this.sheetDeletion(sheetName, id);
                        this.calculateInstance.unregisterGridAsSheet(id, id);
                        this.definedNamesDeletion(visibleName);
                        break;
                    }
                }
                break;
            case 'getReferenceError':
                args.refError = this.referenceError();
                break;
            case 'getAlpha':
                args.col = getAlphalabel(args.col);
                break;
            case 'addCustomFunction':
                this.addCustomFunction(args.functionHandler, args.functionName, args.formulaDescription, args.category);
                break;
            case 'computeExpression':
                args.calcValue = this.calculateInstance.computeExpression(args.formula, args.isFromComputeExpression);
                break;
            case 'registerGridInCalc':
                this.calculateInstance.grid = args.sheetID;
                break;
            case 'dependentCellsAvailable':
            case 'checkFormulaAdded':
                if (family.isSheetMember && !isNullOrUndefined(family.parentObjectToToken)) {
                    args.address = family.parentObjectToToken.get(args.sheetId) + args.address;
                }
                if (action === 'checkFormulaAdded') {
                    args.added = this.calculateInstance.getFormulaInfoTable().has(args.address);
                }
                else {
                    args.isAvailable = this.calculateInstance.getDependentCells().has(args.address);
                }
                break;
            case 'calculateNow':
                this.calculateNow(args);
                break;
            case 'ClearDependentCellCollection':
                this.calculateInstance.getDependentFormulaCells().clear();
                this.calculateInstance.getDependentCells().clear();
                this.calculateInstance.getFormulaInfoTable().clear();
                break;
        }
    }
    initFormulaOnImport() {
        this.registerSheet();
        this.calculateInstance.setParseArgumentSeparator(this.parent.listSeparator);
        this.updateSheetInfo();
        this.initiateDefinedNames();
    }
    definedNamesDeletion(sheetName) {
        const definedNames = this.parent.definedNames;
        if (definedNames && definedNames.length > 0) {
            for (let i = definedNames.length - 1; i >= 0; i--) {
                if (definedNames[i].refersTo.substring(1, definedNames[i].refersTo.lastIndexOf('!')).split('\'').join('') === sheetName) {
                    this.removeDefinedName(definedNames[i].name, definedNames[i].scope);
                }
            }
        }
    }
    referenceError() {
        return this.calculateInstance.getErrorStrings()[CommonErrors.Ref];
    }
    getSheetInfo() {
        return this.sheetInfo;
    }
    addCustomFunction(functionHandler, functionName, formulaDescription, category) {
        this.calculateInstance.defineFunction(functionName, functionHandler, formulaDescription, category);
    }
    updateSheetInfo() {
        this.sheetInfo = [];
        this.parent.sheets.forEach((sheet) => {
            this.sheetInfo.push({ visibleName: sheet.name, sheet: 'Sheet' + sheet.id, index: sheet.id });
        });
    }
    getSheetRefUpdateOnDelete() {
        const definedNames = this.calculateInstance.namedRanges;
        let keyArray;
        let valueArray;
        let isDefinedNamesAvail;
        if (definedNames && definedNames.size) {
            isDefinedNamesAvail = true;
            keyArray = Array.from(definedNames.keys());
            valueArray = Array.from(definedNames.values());
        }
        return (delSheetName, formula) => {
            let isNamedRange;
            if (isDefinedNamesAvail && !formula.includes(delSheetName)) {
                formula = formula.replace(/\w+/g, (key) => {
                    const index = keyArray.indexOf(key);
                    if (index !== -1) {
                        isNamedRange = true;
                        return valueArray[index];
                    }
                    return key;
                });
            }
            const sheetName = delSheetName.toUpperCase();
            formula = formula.toUpperCase();
            let idx = formula.indexOf(sheetName);
            let isUpdate;
            while (idx > -1) {
                isUpdate = true;
                formula = formula.split((formula[idx - 1] === '\'' && formula[idx + sheetName.length] === '\'' ? `'${sheetName}'` : sheetName) +
                    this.calculateInstance.sheetToken).join(this.referenceError());
                idx = formula.indexOf(sheetName);
            }
            return { value: formula, isNamedRange, isUpdate: isUpdate };
        };
    }
    sheetDeletion(delSheetName, sheetId) {
        const dependentCell = this.calculateInstance.getDependentCells();
        let fInfo;
        let token;
        const family = this.calculateInstance.getSheetFamilyItem(sheetId);
        const updateSheetRef = this.getSheetRefUpdateOnDelete();
        let updatedInfo;
        dependentCell.forEach((dependentCellRefs, cellRef) => {
            dependentCellRefs.forEach((dependentCellRef) => {
                fInfo = this.calculateInstance.getFormulaInfoTable().get(dependentCellRef);
                if (!isNullOrUndefined(fInfo)) {
                    updatedInfo = updateSheetRef(delSheetName, fInfo.formulaText);
                    if (updatedInfo.value !== fInfo.formulaText && updatedInfo.isUpdate) {
                        token = dependentCellRef.slice(0, dependentCellRef.lastIndexOf(this.calculateInstance.sheetToken) + 1);
                        updatedInfo.sheetId = family.tokenToParentObject.has(token) ? Number(family.tokenToParentObject.get(token)) :
                            parseInt(dependentCellRef.split('!')[1], 10) + 1;
                        this.updateDataContainer([this.calculateInstance.rowIndex(dependentCellRef) - 1, this.calculateInstance.colIndex(dependentCellRef) - 1], updatedInfo);
                        this.calculateInstance.refresh(fInfo.getParsedFormula());
                    }
                }
                token = cellRef.slice(0, cellRef.lastIndexOf(this.calculateInstance.sheetToken) + 1);
                if (sheetId === (family.tokenToParentObject.has(token) ? family.tokenToParentObject.get(token) :
                    cellRef.split('!')[1])) {
                    this.calculateInstance.getFormulaInfoTable().delete(cellRef);
                    this.calculateInstance.clearFormulaDependentCells(cellRef);
                }
            });
        });
    }
    renameUpdation(args) {
        const name = args.value;
        const pName = args.pName;
        let sheet;
        let cell;
        const uPName = args.pName.toUpperCase();
        const escapeRegx = new RegExp('[!@#$%^&()+=\';,.{}|\\":<>~_-]', 'g');
        const exp = '(?=[\'!])(?=[^"]*(?:"[^"]*"[^"]*)*$)';
        const regExp = RegExp;
        const regx = new regExp(pName.replace(escapeRegx, '\\$&') + exp, 'gi');
        const renameValidationSheetRef = (validation) => {
            if (checkIsFormula(validation.value1) && validation.value1.toUpperCase().includes(uPName) && validation.value1.match(regx)) {
                validation.value1 = validation.value1.replace(regx, name);
            }
            if (checkIsFormula(validation.value2) && validation.value2.toUpperCase().includes(uPName) && validation.value2.match(regx)) {
                validation.value2 = validation.value2.replace(regx, name);
            }
        };
        this.sheetInfo.forEach((info, index) => {
            sheet = getSheet(this.parent, index);
            if (sheet && sheet.rows && sheet.rows.length) {
                for (let i = 0, rowLen = sheet.rows.length; i < rowLen; i++) {
                    if (sheet.rows[i] && sheet.rows[i].cells) {
                        for (let j = 0, cellsLen = sheet.rows[i].cells.length; j < cellsLen; j++) {
                            cell = getCell(i, j, sheet, false, true);
                            if (cell.formula && checkIsFormula(cell.formula) && cell.formula.toUpperCase().includes(uPName) &&
                                cell.formula.match(regx)) {
                                cell.formula = cell.formula.replace(regx, name);
                            }
                            if (cell.validation) {
                                renameValidationSheetRef(cell.validation);
                            }
                            if (cell.chart && cell.chart.length) {
                                cell.chart.forEach((chart) => {
                                    if (chart.range.includes('!')) {
                                        const tokenIdx = chart.range.lastIndexOf('!');
                                        let chartSheetRef = chart.range.substring(0, tokenIdx).toUpperCase();
                                        if (chartSheetRef.startsWith('\'') && chartSheetRef.endsWith('\'')) {
                                            chartSheetRef = chartSheetRef.slice(1, -1);
                                        }
                                        if (chartSheetRef === uPName) {
                                            chart.range = name + chart.range.substring(tokenIdx);
                                        }
                                    }
                                });
                            }
                        }
                    }
                }
            }
            if (sheet && sheet.columns && sheet.columns.length) {
                let column;
                for (let i = 0, colsLen = sheet.columns.length; i < colsLen; i++) {
                    column = sheet.columns[i];
                    if (column && column.validation) {
                        renameValidationSheetRef(column.validation);
                    }
                }
            }
            const definedNames = this.parent.definedNames;
            for (let i = 0; i < definedNames.length; i++) {
                if (checkIsFormula(definedNames[i].refersTo) && definedNames[i].refersTo.includes(pName) &&
                    definedNames[i].refersTo.match(regx)) {
                    definedNames[i].refersTo = definedNames[i].refersTo.replace(regx, name);
                    if (definedNames[i].scope.includes(pName)) {
                        definedNames[i].scope = name;
                    }
                }
            }
            this.calculateInstance.updateNamedRange(pName, name);
            if (info.visibleName === pName) {
                info.visibleName = name;
            }
        });
    }
    updateDataContainer(indexes, data) {
        let sheet;
        let rowData;
        let colObj;
        for (let i = 0, len = this.parent.sheets.length; i < len; i++) {
            sheet = getSheet(this.parent, i);
            if (sheet.id === data.sheetId) {
                if (indexes[0] in sheet.rows) {
                    rowData = sheet.rows[indexes[0]];
                    if (indexes[1] in rowData.cells) {
                        colObj = rowData.cells[indexes[1]];
                        colObj.formula = data.isNamedRange ? colObj.formula : data.value;
                        if (data.visible) {
                            if (i === this.parent.activeSheetIndex && sheet.activeCell === getCellAddress(indexes[0], indexes[1])) {
                                this.parent.notify(selectionComplete, {});
                            }
                        }
                        else if (this.parent.calculationMode === 'Automatic') {
                            colObj.value = this.referenceError();
                        }
                    }
                    else {
                        rowData.cells[indexes[1]] = colObj = {};
                    }
                }
                else {
                    rowData = sheet.rows[indexes[0]] = {};
                    rowData[indexes[1]] = colObj = {};
                }
                break;
            }
        }
    }
    parseSheetRef(value, addSheetQuotes) {
        let regx;
        // eslint-disable-next-line no-useless-escape
        const escapeRegx = new RegExp('[!@#$%^&()+=\';,.{}|\":<>~_-]', 'g');
        let i = 0;
        const sheetInfo = this.getSheetInfo();
        const sheetCount = sheetInfo.length;
        const temp = [];
        temp.length = 0;
        let regxTemp;
        let searchIdx;
        let idx;
        let valSearchIdx;
        let regxVisible;
        const exp = '(?=[\'!])(?=[^"]*(?:"[^"]*"[^"]*)*$)';
        const regExp = RegExp;
        for (i = 0; i < sheetCount; i++) {
            if (sheetInfo[i].sheet !== sheetInfo[i].visibleName) {
                regx = new regExp(sheetInfo[i].visibleName.replace(escapeRegx, '\\$&') + exp, 'gi');
                idx = i;
                if (value.match(regx)) {
                    for (let j = i + 1; j < sheetCount; j++) {
                        if (sheetInfo[j].visibleName.includes(sheetInfo[i].visibleName)) {
                            regxTemp = new regExp(sheetInfo[j].visibleName.replace(escapeRegx, '\\$&') + exp, 'gi');
                            searchIdx = value.search(regxTemp);
                            valSearchIdx = value.search(regx);
                            if (searchIdx > -1 && (searchIdx < valSearchIdx || (searchIdx === valSearchIdx &&
                                sheetInfo[j].visibleName.length > sheetInfo[i].visibleName.length))) {
                                regxVisible = new RegExp('Sheet', 'gi');
                                if (sheetInfo[j].visibleName.search(regxVisible) !== 0) {
                                    regx = regxTemp;
                                    idx = j;
                                }
                            }
                        }
                    }
                    value = value.replace(regx, idx + String.fromCharCode(143));
                    temp.push(idx);
                }
            }
        }
        i = 0;
        let sheetRef;
        while (i < temp.length) {
            regx = new regExp(temp[i] + String.fromCharCode(143) + exp, 'gi');
            sheetRef = addSheetQuotes ? '`' + sheetInfo[temp[i]].sheet + '`' : sheetInfo[temp[i]].sheet;
            value = value.replace(regx, sheetRef);
            i++;
        }
        return value;
    }
    registerSheet(sheetIndex = 0, sheetCount = this.parent.sheets.length) {
        let id;
        while (sheetIndex < sheetCount) {
            id = getSheet(this.parent, sheetIndex).id + '';
            this.calculateInstance.registerGridAsSheet(id, id, this.calcID);
            sheetIndex++;
        }
    }
    unRegisterSheet(sheetIndex = 0, sheetCount = this.parent.sheets.length, propertyChange, isNewWorkBook) {
        let id;
        this.calculateInstance.tokenCount = 0;
        if (propertyChange) {
            this.calculateInstance.unregisterGridAsSheet(id, id, propertyChange);
        }
        else {
            if (isNewWorkBook) {
                for (let i = this.sheetInfo.length - 1; i >= 0; i--) {
                    const visibleName = this.sheetInfo[i].visibleName;
                    id = this.sheetInfo[i].index.toString();
                    this.calculateInstance.unregisterGridAsSheet(id, id);
                    this.definedNamesDeletion(visibleName);
                }
                this.clearFormulaDependentCells({ isOpen: true });
            }
            else {
                while (sheetIndex < sheetCount) {
                    id = getSheet(this.parent, sheetIndex).id + '';
                    this.calculateInstance.unregisterGridAsSheet(id, id);
                    sheetIndex++;
                }
            }
        }
    }
    getUniqueRange(args) {
        args.range = this.calculateInstance.uniqueRange;
    }
    getSortRange(args) {
        args.range = this.calculateInstance.sortRange;
    }
    removeUniquecol(args) {
        if (args && args.clearAll) {
            this.clearAllUniqueFormulaValue();
            return;
        }
        const sheet = this.parent.getActiveSheet();
        for (let i = 0; i < this.calculateInstance.uniqueRange.length; i++) {
            const uniqRngAddress = this.calculateInstance.uniqueRange[i].split(':')[0].split('!');
            if (uniqRngAddress[0] === sheet.name && uniqRngAddress[1] === sheet.activeCell) {
                const range = getRangeIndexes(this.calculateInstance.uniqueRange[i]);
                this.calculateInstance.uniqueRange.splice(i, 1);
                for (let j = range[0]; j <= range[2]; j++) {
                    for (let k = range[1]; k <= range[3]; k++) {
                        const cell = getCell(j, k, this.parent.getActiveSheet());
                        cell.formula = '';
                        this.parent.updateCellDetails({ value: '', formula: '' }, getRangeAddress([j, k]), undefined, undefined, true);
                    }
                }
            }
        }
    }
    removeSortcol(args) {
        if (args && args.clearAll) {
            this.clearAllSortFormulaValue();
            return;
        }
        const sheet = this.parent.getActiveSheet();
        for (let i = 0; i < this.calculateInstance.sortRange.length; i++) {
            const sortRngAddress = this.calculateInstance.sortRange[i].split(':')[0].split('!');
            if (sortRngAddress[0] === sheet.name && sortRngAddress[1] === sheet.activeCell) {
                const range = getRangeIndexes(this.calculateInstance.sortRange[i]);
                this.calculateInstance.sortRange.splice(i, 1);
                for (let j = range[0]; j <= range[2]; j++) {
                    for (let k = range[1]; k <= range[3]; k++) {
                        const cell = getCell(j, k, this.parent.getActiveSheet());
                        cell.formula = '';
                        this.parent.updateCellDetails({ value: '', formula: '' }, getRangeAddress([j, k]), undefined, undefined, true);
                    }
                }
            }
        }
    }
    /**
     * Perform the formula calculation.
     *
     * @param {FormulaCalculateArgs} args - Specifies the formula calculation options.
     * @param {number} args.rowIndex - The index of the row.
     * @param {number} args.colIndex - The index of the column.
     * @param {string} args.value - The value of the cell.
     * @param {boolean} args.isFormula - A flag indicating whether the value is a formula.
     * @param {number} args.sheetIdx - The index of the sheet.
     * @param {boolean} args.isRefreshing - A flag indicating whether the calculation is being refreshed.
     * @param {boolean} [args.isDependentRefresh] - An optional flag indicating whether the refresh is dependent.
     * @param {boolean} [args.isRandomFormula] - An optional flag indicating whether the formula is random.
     * @param {boolean} [args.isDelete] - An optional flag indicating whether is from delete cells.
     * @param {number[]} [args.deletedRange] - An optional range array indicating the deleted cells.
     * @returns {void}
     * @private
     */
    refreshCalculate(args) {
        args.sheet = isNullOrUndefined(args.sheetIndex) ? this.parent.getActiveSheet() : getSheet(this.parent, args.sheetIndex);
        const sheetId = args.sheet.id + '';
        const family = this.calculateInstance.getSheetFamilyItem(sheetId);
        let cellRef = getColumnHeaderText(args.colIndex + 1) + (args.rowIndex + 1);
        if (family.isSheetMember && !isNullOrUndefined(family.parentObjectToToken)) {
            cellRef = family.parentObjectToToken.get(sheetId) + cellRef;
        }
        if (args.isFormula) {
            this.calculateFormula(args, cellRef);
        }
        else {
            if (this.calculateInstance.getFormulaInfoTable().has(cellRef)) {
                this.calculateInstance.getFormulaInfoTable().delete(cellRef);
                this.calculateInstance.clearFormulaDependentCells(cellRef);
            }
            this.calculateInstance.refresh(cellRef, null, null, null, args.isDelete, args.deletedRange);
            if (this.parent.calculationMode === 'Automatic') {
                this.calculateInstance.refreshRandValues(cellRef);
            }
        }
        this.calculateInstance.cell = '';
        args.isFormulaDependent = this.calculateInstance.getDependentCells().has(cellRef);
        if (args.value) {
            args.value = args.value.toString().split('^+').join('^').split('&+').join('&');
        }
    }
    calculateFormula(args, cellRef) {
        const sheet = args.sheet;
        this.autoCorrectFormula(args, sheet);
        let value = args.value;
        const checkFormulaStr = (value || '').toUpperCase();
        if (args.isClipboard && (checkFormulaStr.includes('UNIQUE') || checkFormulaStr.includes('SORT'))) {
            setCell(args.rowIndex, args.colIndex, sheet, { value: '' }, true);
        }
        let formula = value;
        value = this.parseSheetRef(value);
        const cellArgs = new ValueChangedArgs(args.rowIndex + 1, args.colIndex + 1, value);
        const usedRange = [sheet.usedRange.rowIndex, sheet.usedRange.colIndex];
        this.calculateInstance.valueChanged(sheet.id.toString(), cellArgs, true, usedRange, args.isRefreshing, sheet.name, args.isRandomFormula, null, args.isDelete, args.deletedRange, args.isDependentRefresh, args.action);
        if (this.calculateInstance.isRandomVal === true && !args.isRandomFormula && this.parent.calculationMode === 'Automatic') {
            this.refreshRandomFormula();
        }
        const updatedCell = getCell(args.rowIndex, args.colIndex, sheet);
        if (updatedCell && formula && !args.isDependentRefresh) {
            formula = formula.toUpperCase();
            let formulaStr;
            if (formula.indexOf('=SUM(') === 0) {
                formulaStr = '=SUM(';
            }
            else if (formula.indexOf('=AVERAGE(') === 0) {
                formulaStr = '=AVERAGE(';
            }
            else if (formula.indexOf('=ROUNDDOWN(') === 0) {
                formulaStr = '=ROUNDDOWN(';
            }
            else if (formula.indexOf('=ROUNDUP(') === 0) {
                formulaStr = '=ROUNDUP(';
            }
            else if (formula.indexOf('=MOD(') === 0) {
                formulaStr = '=MOD(';
            }
            if (formulaStr) {
                formula = formula.replace(formulaStr, '');
                if (formula.includes(')')) {
                    formula = formula.slice(0, formula.lastIndexOf(')'));
                    let fStr;
                    let idx;
                    while (formula.includes('(') && formula.includes(')')) {
                        idx = formula.indexOf('(');
                        fStr = formula.slice(idx + 1);
                        formula = formula.slice(0, idx) + (fStr.includes(')') ? fStr.slice(fStr.indexOf(')') + 1) : fStr);
                    }
                }
                const cellRefArr = formula.split(this.calculateInstance.getParseArgumentSeparator());
                let cellRef;
                let fCell;
                let model;
                let sheetIdx;
                let sheetName;
                let index;
                for (let idx = 0; idx < cellRefArr.length; idx++) {
                    cellRef = cellRefArr[idx].split(':')[0];
                    if (cellRef.includes('!')) {
                        sheetName = cellRef.substring(0, cellRef.lastIndexOf('!')).split('\'').join('');
                        cellRef = cellRef.substring(cellRef.lastIndexOf('!') + 1);
                    }
                    else {
                        sheetName = '';
                    }
                    if (isCellReference(cellRef)) {
                        if (sheetName) {
                            sheetIdx = getSheetIndex(this.parent, sheetName);
                            model = sheetIdx !== undefined ? getSheet(this.parent, sheetIdx) : sheet;
                        }
                        else {
                            model = sheet;
                        }
                        index = getRangeIndexes(cellRef);
                        fCell = getCell(index[0], index[1], model);
                        const format = getTypeFromFormat(updatedCell.format);
                        const excludedFormats = ['Number', 'Currency', 'LongDate', 'Time'];
                        if (fCell && fCell.format && (!updatedCell.format || (!args.fillType &&
                            (excludedFormats.every((fmt) => format !== fmt) &&
                                getTypeFromFormat(fCell.format) !== 'Number')))) {
                            updatedCell.format = fCell.format;
                            break;
                        }
                    }
                }
            }
            else {
                const depCells = this.calculateInstance.getDependentFormulaCells().get(cellRef);
                if (depCells && depCells.size && this.calculateInstance.getFormulaInfoTable().has(cellRef) &&
                    this.calculateInstance.getFormulaInfoTable().get(cellRef).getParsedFormula().lastIndexOf('q') === -1 &&
                    !updatedCell.format) {
                    let format;
                    let fCell;
                    let sheetRef;
                    let model;
                    let sheetIdx;
                    let idx;
                    const family = this.calculateInstance.getSheetFamilyItem(null, this.calcID);
                    depCells.forEach((cellRef) => {
                        if (!format) {
                            sheetRef = cellRef.slice(0, cellRef.lastIndexOf('!') + 1);
                            cellRef = cellRef.replace(sheetRef, '');
                            if (isCellReference(cellRef)) {
                                idx = getRangeIndexes(cellRef);
                                if (family.tokenToParentObject.has(sheetRef)) {
                                    sheetIdx = getSheetIndexFromId(this.parent, Number(family.tokenToParentObject.get(sheetRef)));
                                    model = sheetIdx !== undefined ? getSheet(this.parent, sheetIdx) : sheet;
                                }
                                else {
                                    model = sheet;
                                }
                                fCell = getCell(idx[0], idx[1], model);
                                if (fCell && fCell.format) {
                                    format = fCell.format;
                                }
                            }
                        }
                    });
                    if (format) {
                        updatedCell.format = format;
                    }
                }
            }
        }
    }
    refreshRandomFormula() {
        let rowId;
        let colId;
        let refValue = '';
        const referenceCollection = this.calculateInstance.randCollection;
        if (this.calculateInstance.randomValues.size > 1 && this.calculateInstance.randomValues.size ===
            referenceCollection.length) {
            for (let i = 0; i < this.calculateInstance.randomValues.size; i++) {
                rowId = this.calculateInstance.rowIndex(referenceCollection[i]);
                colId = this.calculateInstance.colIndex(referenceCollection[i]);
                refValue = this.calculateInstance.randomValues.get(referenceCollection[i]);
                const sheetId = (parseFloat(this.calculateInstance.getSheetToken(referenceCollection[i]).split(this.calculateInstance.sheetToken).join('')) + 1).toString();
                const sheet = getSheet(this.parent, getSheetIndexFromId(this.parent, Number(sheetId)));
                if (sheet && getCell(rowId - 1, colId - 1, sheet).formula) {
                    const tempArgs = new ValueChangedArgs(rowId, colId, refValue);
                    this.calculateInstance.valueChanged(sheetId, tempArgs, true, undefined, undefined, undefined, false, true);
                }
            }
        }
    }
    autoCorrectFormula(args, sheet) {
        if (!isNullOrUndefined(args.value)) {
            let formula = args.value;
            formula = this.autoCorrectCellRef({ formula: args.value });
            formula = formula.toString();
            if (formula.split('(').length === 2 && formula.indexOf(')') < 0) {
                formula += ')';
            }
            let isEqual;
            if (formula.indexOf('=') === 0) {
                formula = formula.slice(1);
                isEqual = true;
            }
            const lessEq = formula.match(/</g);
            const greaterEq = formula.match(/>/g);
            const equal = formula.match(/=/g);
            if (lessEq) {
                let lessOp = '';
                for (let i = 0; i < lessEq.length; i++) {
                    lessOp = lessOp + lessEq[i];
                }
                formula = formula.replace(lessOp, '<');
            }
            if (greaterEq) {
                let greaterOp = '';
                for (let j = 0; j < greaterEq.length; j++) {
                    greaterOp = greaterOp + greaterEq[j];
                }
                formula = formula.replace(greaterOp, '>');
            }
            if (equal) {
                let equalOp = '';
                for (let c = 0; c < equal.length; c++) {
                    equalOp = equalOp + equal[c];
                }
                formula = formula.split(equalOp).join('=');
            }
            formula = isEqual ? '=' + formula : formula;
            if (lessEq || greaterEq || equal) {
                getCell(args.rowIndex, args.colIndex, sheet).formula = formula;
            }
            args.value = formula;
        }
    }
    correctCellReference(cellRef) {
        const cellRefArr = cellRef.split(':');
        let refArr;
        let sheetRefArr;
        let oprMatchArr;
        let isInvalid;
        let updatedRef;
        cellRefArr.forEach((cellAddr, idx) => {
            sheetRefArr = cellAddr.split('!');
            cellRef = sheetRefArr[1] || cellAddr;
            updatedRef = null;
            if (cellRef.includes('&')) {
                refArr = cellRef.split('&');
                if (this.calculateInstance.isCellReference(refArr[1].split('$').join(''))) {
                    refArr[1] = this.getUpdatedCellRef(refArr[1]);
                    updatedRef = refArr.join('&');
                }
            }
            else if (this.calculateInstance.isCellReference(cellRef.split('$').join(''))) {
                updatedRef = this.getUpdatedCellRef(cellRef);
                if (sheetRefArr.length > 1) {
                    updatedRef = sheetRefArr[0] + '!' + updatedRef;
                }
            }
            else {
                oprMatchArr = cellAddr.match(/[/+\-*^><>=<=<>]+/g);
                if (oprMatchArr) {
                    refArr = cellAddr.split(oprMatchArr[0]);
                    for (let refIdx = 0; refIdx < refArr.length; refIdx++) {
                        sheetRefArr = refArr[refIdx].split('!');
                        cellRef = sheetRefArr[1] || sheetRefArr[0];
                        if (this.calculateInstance.isCellReference(cellRef.split('$').join(''))) {
                            refArr[refIdx] = this.getUpdatedCellRef(cellRef);
                            if (sheetRefArr.length > 1) {
                                refArr[refIdx] = sheetRefArr[0] + '!' + refArr[refIdx];
                            }
                        }
                    }
                    updatedRef = refArr.join(oprMatchArr[0]);
                }
            }
            if (updatedRef && updatedRef !== cellAddr) {
                isInvalid = true;
                cellRefArr[idx] = updatedRef;
            }
        });
        return { isInvalid: isInvalid, ref: cellRefArr.join(':') };
    }
    autoCorrectCellRef(args) {
        const rightParens = args.formula.lastIndexOf(')');
        let refCorrectObj;
        if (rightParens > -1 && args.formula.split(')').length === 2) {
            let leftParens = rightParens - 1;
            while (leftParens > -1 && args.formula[leftParens] !== '(') {
                if (args.formula[leftParens] === ')') {
                    return args.formula;
                }
                leftParens--;
            }
            if (leftParens > -1) {
                const formulaArgs = args.formula.substring(leftParens + 1, rightParens);
                const listSeparator = this.calculateInstance.getParseArgumentSeparator();
                const formulaArgsArr = formulaArgs.split(listSeparator);
                let isInValidRef;
                for (let argsIdx = 0; argsIdx < formulaArgsArr.length; argsIdx++) {
                    refCorrectObj = this.correctCellReference(formulaArgsArr[argsIdx]);
                    if (refCorrectObj.isInvalid) {
                        isInValidRef = true;
                        formulaArgsArr[argsIdx] = refCorrectObj.ref;
                    }
                }
                if (isInValidRef) {
                    args.formula = args.formula.split(formulaArgs).join(formulaArgsArr.join(listSeparator));
                    args.isInvalid = true;
                }
            }
        }
        else if (args.formula.startsWith('=') && !args.formula.includes(')')) {
            refCorrectObj = this.correctCellReference(args.formula.substring(1, args.formula.length));
            if (refCorrectObj.isInvalid) {
                args.formula = '=' + refCorrectObj.ref;
                args.isInvalid = true;
            }
        }
        return args.formula;
    }
    getUpdatedCellRef(cellRef) {
        const orgCellRef = cellRef;
        cellRef = cellRef.trim();
        const isAbsolute = cellRef.indexOf('$') === 0;
        let alphabetStartIdx = cellRef.search(/[a-zA-Z]/);
        const digitStartIdx = cellRef.search(/\d/);
        alphabetStartIdx = isAbsolute ? alphabetStartIdx - 1 : alphabetStartIdx;
        if ((isAbsolute ? digitStartIdx > 1 : digitStartIdx > 0) && isNumber(cellRef.substring(digitStartIdx, cellRef.length))) {
            return orgCellRef;
        }
        else {
            return cellRef.substring(alphabetStartIdx, cellRef.length) + cellRef.substring(0, alphabetStartIdx);
        }
    }
    initiateDefinedNames() {
        const definedNames = this.parent.definedNames;
        let i = 0;
        while (i < definedNames.length) {
            const definedname = definedNames[i];
            const refersTo = this.parseSheetRef(definedname.refersTo);
            let range = getRangeFromAddress(refersTo);
            let cellRef = false;
            const isLink = refersTo.indexOf('http:') > -1 ? true : (refersTo.indexOf('https:') > -1 ? true : false);
            range = range.split('$').join('');
            range = range.split('=').join('');
            if (range.indexOf(':') > -1) {
                const rangeSplit = range.split(':');
                if ((isCellReference(rangeSplit[0]) && isCellReference(rangeSplit[1])) ||
                    ((rangeSplit[0].match(/[0-9]/) && rangeSplit[1].match(/[0-9]/)) ||
                        (rangeSplit[0].toUpperCase().match(/[A-Z]/) && rangeSplit[1].toUpperCase().match(/[A-Z]/)))) {
                    cellRef = true;
                }
            }
            else if (range.indexOf(':') < 0) {
                if (isCellReference(range)) {
                    cellRef = true;
                }
            }
            if (isLink) {
                cellRef = false;
            }
            if (cellRef) {
                this.addDefinedName(definedname, true, undefined, true);
            }
            else {
                this.removeDefinedName(definedname.name, definedname.scope, true);
                i--;
            }
            i++;
        }
    }
    /**
     * @hidden
     * Used to add defined name to workbook.
     *
     * @param {DefineNameModel} definedName - Define named range.
     * @param {boolean} isValidate - Specify the boolean value.
     * @param {number} index - Define named index.
     * @param {boolean} isEventTrigger - Specify the boolean value.
     * @returns {boolean} - Used to add defined name to workbook.
     */
    addDefinedName(definedName, isValidate, index, isEventTrigger) {
        if (index === undefined || index < -1) {
            index = this.parent.definedNames.length;
        }
        let isAdded = true;
        let sheetIdx;
        let name = definedName.name;
        if (definedName.refersTo.indexOf('!') < 0) {
            let sheetName = getSheetName(this.parent);
            sheetName = sheetName.indexOf(' ') !== -1 ? '\'' + sheetName + '\'' : sheetName;
            definedName.refersTo = sheetName + '!' + ((definedName.refersTo.indexOf('=') < 0) ?
                definedName.refersTo : definedName.refersTo.split('=')[1]);
        }
        const visibleRefersTo = definedName.refersTo;
        const refersTo = this.parseSheetRef(definedName.refersTo);
        if (definedName.scope) {
            sheetIdx = getSheetIndex(this.parent, definedName.scope);
            if (sheetIdx > -1) {
                name = getSheetName(this.parent, sheetIdx) + '!' + name;
            }
        }
        else {
            definedName.scope = 'Workbook';
        }
        if (!definedName.comment) {
            definedName.comment = '';
        }
        //need to extend once internal sheet value changes done.
        if (!isValidate && this.checkIsNameExist(definedName.name, definedName.scope)) {
            isAdded = false;
        }
        else {
            this.calculateInstance.addNamedRange(name, refersTo[0] === '=' ? refersTo.substr(1) : refersTo);
            if (refersTo[0] !== '=') {
                definedName.refersTo = '=' + visibleRefersTo;
            }
            if (this.parent.definedNames.indexOf(definedName) < 0) {
                this.parent.definedNames.splice(index, 0, definedName);
                this.parent.notify(formulaBarOperation, { action: 'setNameBoxValue', definedName: definedName });
            }
        }
        const eventArgs = { name: definedName.name, scope: definedName.scope, comment: definedName.comment,
            refersTo: definedName.refersTo, cancel: false };
        if (!isEventTrigger) {
            this.parent.notify('actionComplete', { eventArgs: eventArgs, action: 'addDefinedName' });
        }
        return isAdded;
    }
    /**
     * @hidden
     * Used to remove defined name from workbook.
     *
     * @param {string} name - Specifies the defined name.
     * @param {string} scope - Specifies the scope of the define name.
     * @param {boolean} isEventTrigger - Specify the boolean value.
     * @returns {boolean} - To Return the bool value.
     */
    removeDefinedName(name, scope, isEventTrigger) {
        let isRemoved = false;
        const scopeVal = !scope ? 'Workbook' : scope;
        const index = this.getIndexFromNameColl(name, scopeVal);
        if (index > -1) {
            let calcName = name;
            if (scope) {
                const sheetIdx = getSheetIndex(this.parent, scope);
                if (sheetIdx > -1) {
                    calcName = getSheetName(this.parent, sheetIdx) + '!' + name;
                }
            }
            this.calculateInstance.removeNamedRange(calcName);
            const removedName = this.parent.definedNames.splice(index, 1);
            this.parent.notify(formulaBarOperation, { action: 'setNameBoxValue', definedName: removedName[0], isRemove: true });
            if (!isEventTrigger) {
                const eventArgs = { name: name, scope: scopeVal, cancel: false };
                this.parent.notify('actionComplete', { eventArgs: eventArgs, action: 'removeDefinedName' });
            }
            isRemoved = true;
        }
        return isRemoved;
    }
    checkIsNameExist(name, sheetName) {
        const isExist = this.parent.definedNames.some((key) => {
            return key.name === name && (sheetName ? key.scope === sheetName : key.scope === '');
        });
        return isExist;
    }
    getIndexFromNameColl(definedName, scope = '') {
        let index = -1;
        this.parent.definedNames.filter((name, idx) => {
            if (name.name === definedName && name.scope === scope) {
                index = idx;
            }
        });
        return index;
    }
    calculateNow(args) {
        const deferred = new Deferred();
        args.promise = deferred.promise;
        let dependentCells;
        const initCalculate = () => {
            let family;
            let token;
            let sheetId;
            let cellRef;
            const options = { isRefreshing: true, action: 'calculate' };
            const formulaInfo = this.calculateInstance.getFormulaInfoTable();
            args.sheets.forEach((sheet) => {
                sheetId = sheet.id.toString();
                family = this.calculateInstance.getSheetFamilyItem(sheetId);
                token = family.isSheetMember ? family.parentObjectToToken.get(sheetId) : '';
                this.parent.setSheetPropertyOnMute(sheet, 'isSheetCalculated', true);
                options.sheet = sheet;
                sheet.rows.forEach((row, rowIdx) => {
                    options.rowIndex = rowIdx;
                    if (row && row.cells) {
                        row.cells.forEach((cell, colIdx) => {
                            if (cell && checkIsFormula(cell.formula)) {
                                cellRef = token + getColumnHeaderText(colIdx + 1) + (rowIdx + 1);
                                if (cell.value === undefined || cell.value === null || !formulaInfo.has(cellRef) || (dependentCells &&
                                    dependentCells[cellRef])) {
                                    options.colIndex = colIdx;
                                    options.value = cell.formula;
                                    this.calculateFormula(options, cellRef);
                                }
                            }
                        });
                    }
                });
            });
            deferred.resolve();
            this.calculateInstance.cell = '';
        };
        let totalLoadCount = args.sheets.length;
        const loadCompleteHandler = () => {
            totalLoadCount--;
            if (!totalLoadCount) {
                initCalculate();
                if (this.parent.calculationMode === 'Automatic' && args.scope === 'CalculateWorkbook') {
                    this.parent.setProperties({ calculationMode: 'Manual' }, true);
                }
                const sheet = this.parent.getActiveSheet();
                if (sheet.conditionalFormats && sheet.conditionalFormats.length) {
                    this.parent.notify(applyCF, { indexes: [], isAction: true, refreshAll: true, isEdit: true });
                }
            }
        };
        const getDependentCellsCheckFn = (sheet) => {
            const sheetId = sheet.id.toString();
            const family = this.calculateInstance.getSheetFamilyItem(sheetId);
            const token = family.isSheetMember ? family.parentObjectToToken.get(sheetId) : '';
            dependentCells = {};
            const dependentCellsInfo = this.calculateInstance.getDependentCells();
            return (rowIdx, colIdx) => {
                const cellAddr = token + getCellAddress(rowIdx, colIdx);
                if (dependentCellsInfo.has(cellAddr)) {
                    dependentCellsInfo.get(cellAddr).forEach((cellRef) => {
                        if (!dependentCells[cellRef]) {
                            dependentCells[cellRef] = true;
                        }
                    });
                }
            };
        };
        args.sheets.forEach((sheet) => {
            if (sheet.ranges.some((range) => range.dataSource && (!range.info || !range.info.loadedRange ||
                !range.info.loadedRange.length))) {
                this.parent.notify(updateSheetFromDataSource, { sheet: sheet, autoDetectFormat: true, loadFromStartCell: true,
                    updateDependentCellsCallback: getDependentCellsCheckFn(sheet), loadComplete: loadCompleteHandler });
            }
            else {
                loadCompleteHandler();
            }
        });
    }
    toFixed(value) {
        const num = Number(value);
        if (Math.round(num) !== num) {
            value = num.toFixed(2);
        }
        return value;
    }
    commputeFormulaValue(args) {
        const parsedSheetValue = this.parseSheetRef(args.value);
        args.value = this.calculateInstance.computeExpression(parsedSheetValue).toString();
    }
    getCellRefValue(args) {
        let sheetId;
        const sheetInfo = this.getSheetInfo();
        const sheetCount = sheetInfo.length;
        const token = this.calculateInstance.sheetToken;
        const value = args.value;
        const tokenIndex = value.lastIndexOf(token);
        if (tokenIndex !== -1) {
            let sheetName = value.substring(0, tokenIndex);
            if (sheetName.length > 0 && sheetName[0] === this.calculateInstance.getFormulaCharacter()) {
                sheetName = sheetName.substring(1, args.value.length); // To get the sheetname without '=' symbol
            }
            for (let i = 0; i < sheetCount; i++) {
                if (sheetName.toUpperCase() === sheetInfo[i].visibleName.toUpperCase()) {
                    sheetId = i;
                    break;
                }
            }
            args.value = this.calculateInstance.getValueFromArg(token + sheetId + token + value.substring(tokenIndex + 1).toUpperCase());
        }
        else if (value.length > 0 && value[0] === this.calculateInstance.getFormulaCharacter()) {
            args.value = this.calculateInstance.getValueFromArg(value.substring(1, args.value.length).toUpperCase());
        }
    }
    aggregateComputation(args) {
        const sheet = this.parent.getActiveSheet();
        let totalCount = 0;
        let totalSum = 0;
        let numericCellsCount = 0;
        let overallMin = Number.POSITIVE_INFINITY;
        let overallMax = Number.NEGATIVE_INFINITY;
        let row;
        let col;
        let cell;
        const autoDetectFormatFn = getAutoDetectFormatParser(this.parent);
        const performCalculation = () => {
            cell = getCell(row, col, sheet);
            if (!cell || isHiddenCol(sheet, col)) {
                return;
            }
            if (cell.formula && isNullOrUndefined(cell.value)) {
                this.parent.notify(calculateFormula, {
                    cell: cell, rowIdx: row, colIdx: col, sheetIndex: this.parent.activeSheetIndex
                });
            }
            autoDetectFormatFn(cell);
            if (cell.value || cell.value === 0) {
                totalCount++;
                if (isNumber(cell.value)) {
                    args.countOnly = false;
                    const numValue = Number(cell.value);
                    totalSum += numValue;
                    numericCellsCount++;
                    overallMin = Math.min(overallMin, numValue);
                    overallMax = Math.max(overallMax, numValue);
                }
            }
        };
        let checkAndCalculate;
        const selectedRanges = sheet.selectedRange.split(' ');
        const cellAddress = new Set();
        if (selectedRanges.length > 1) {
            checkAndCalculate = () => {
                const key = `${row}${col}`;
                if (cellAddress.has(key)) {
                    return;
                }
                cellAddress.add(key);
                performCalculation();
            };
        }
        else {
            checkAndCalculate = () => performCalculation();
        }
        selectedRanges.forEach((range) => {
            const indexes = getSwapRange(getRangeIndexes(range));
            if (indexes[2] > sheet.usedRange.rowIndex) {
                indexes[2] = sheet.usedRange.rowIndex;
            }
            if (indexes[3] > sheet.usedRange.colIndex) {
                indexes[3] = sheet.usedRange.colIndex;
            }
            for (row = indexes[0]; row <= indexes[2]; row++) {
                if (isHiddenRow(sheet, row)) {
                    continue;
                }
                for (col = indexes[1]; col <= indexes[3]; col++) {
                    checkAndCalculate();
                }
            }
        });
        args.Count = totalCount;
        if (!args.Count || args.countOnly) {
            return;
        }
        const finalAvg = numericCellsCount > 0 ? totalSum / numericCellsCount : 0;
        const aggregateValues = [totalSum, finalAvg, overallMin, overallMax];
        const formatedValues = [];
        const activeIndex = getRangeIndexes(sheet.activeCell);
        cell = getCell(activeIndex[0], activeIndex[1], sheet, false, true);
        aggregateValues.forEach((aggregateVal) => {
            let calcValue = aggregateVal.toString();
            if (cell.format && cell.format !== 'General') {
                const eventArgs = { formattedText: calcValue, value: calcValue, format: cell.format,
                    cell: { value: calcValue, format: cell.format } };
                this.parent.notify(getFormattedCellObject, eventArgs);
                calcValue = eventArgs.formattedText;
            }
            else {
                calcValue = this.toFixed(calcValue);
            }
            formatedValues.push(calcValue);
        });
        args.Sum = formatedValues[0];
        args.Avg = formatedValues[1];
        args.Min = formatedValues[2];
        args.Max = formatedValues[3];
        if ((totalCount - numericCellsCount) > numericCellsCount) {
            args.isMaxNonNumericCells = true;
        }
    }
    refreshInsertDelete(args) {
        if (args.modelType === 'Sheet') {
            return;
        }
        const formulaDependentCells = this.calculateInstance.getDependentFormulaCells();
        let cell;
        const sheetIndex = getSheetIndexFromId(this.parent, args.sheet.id);
        this.parent.sheets.forEach((sheet, index) => {
            for (let i = 0, rowLen = sheet.usedRange.rowIndex; i <= rowLen; i++) {
                for (let j = 0, colLen = sheet.usedRange.colIndex; j <= colLen; j++) {
                    cell = getCell(i, j, sheet, false, true);
                    if (cell.formula && checkIsFormula(cell.formula)) {
                        if (index === sheetIndex) {
                            if (args.isInsert || !(args.modelType === 'Row' ? i >= args.startIndex && i <= args.endIndex :
                                j >= args.startIndex && j <= args.endIndex)) {
                                this.updateFormula(args, cell, i, j, sheetIndex);
                            }
                        }
                        else if (cell.formula.includes(args.sheet.name)) {
                            this.updateFormula(args, cell, i, j, sheetIndex, true, sheet);
                        }
                    }
                }
            }
        });
        formulaDependentCells.clear();
        this.calculateInstance.getDependentCells().clear();
        this.calculateInstance.getFormulaInfoTable().clear();
        this.refreshNamedRange(args);
    }
    getUpdatedFormulaOnInsertDelete(args) {
        if (args.sheetNames) {
            if (!args.updateSheetRef) {
                args.updateSheetRef = this.getSheetRefUpdateOnDelete();
            }
            const previousFormula = this.parseSheetRef(args.cell.formula, false);
            let formula = previousFormula;
            args.sheetNames.forEach((sheetName) => {
                const updatedInfo = args.updateSheetRef(sheetName, formula);
                if (updatedInfo.isUpdate) {
                    formula = updatedInfo.value;
                }
            });
            if (formula !== previousFormula) {
                args.cell.formula = formula;
            }
        }
        else {
            this.updateFormula(args.insertDeleteArgs, args.cell, args.row, args.col, args.sheetIdx, args.otherSheet, args.formulaSheet);
        }
    }
    updateFormula(args, cell, row, col, sheetIdx, otherSheet, formulaSheet) {
        let ref;
        let pVal;
        let index;
        let updated;
        let isRangeFormula;
        const containAlphabetAndDigit = new RegExp(/^(?=.*[a-zA-Z])(?=.*\d)/g);
        let isValidCellReference;
        let isFullColumn;
        if (cell.formula && cell.formula.includes('UNIQUE') && cell.value !== '#SPILL!' && row !== undefined) {
            this.clearUniqueRange(row, col, formulaSheet || args.sheet);
        }
        if (cell.formula && cell.formula.includes('SORT') && cell.value !== '#SPILL!' && row !== undefined) {
            this.clearSortRange(row, col, formulaSheet || args.sheet);
        }
        const getAddress = () => {
            let range = (isAbsoluteRef ? '$' : '') + getColumnHeaderText(index[1] + 1) + (isAbsoluteRef ? '$' : '') + (index[0] + 1);
            if (index[0] !== index[2] || index[1] !== index[3]) {
                range += ':' + (isAbsoluteRef ? '$' : '') + getColumnHeaderText(index[3] + 1) + (isAbsoluteRef ? '$' : '') + (index[2] + 1);
            }
            return range;
        };
        const formulaArr = this.parseFormulaArgument({ formula: this.parseSheetRef(cell.formula, true), rangeRef: true });
        const sheetInfo = this.getSheetInfo();
        let sheetName;
        let refChanged;
        let isAbsoluteRef;
        const isSingleRangeRef = !cell.formula.includes(this.parent.listSeparator);
        for (let i = 0; i < formulaArr.length; i++) {
            ref = formulaArr[i].trim();
            isAbsoluteRef = ref.includes('$');
            if (isAbsoluteRef) {
                ref = ref.replace(/[$]/g, '');
            }
            isValidCellReference = true;
            if (this.calculateInstance.isCellReference(ref)) {
                isRangeFormula = ref.includes(':');
                pVal = i && formulaArr[i - 1].trim();
                if (pVal && pVal[pVal.length - 1] === '!') {
                    pVal = pVal.replace(/['!]/g, '');
                    sheetName = sheetInfo[sheetIdx].sheet === sheetInfo[sheetIdx].visibleName ? args.sheet.name :
                        '`' + sheetInfo[sheetIdx].sheet + '`';
                    if (pVal !== sheetName) {
                        continue;
                    }
                }
                else if (otherSheet) {
                    continue;
                }
                if (!containAlphabetAndDigit.test(ref) && ref.indexOf(':') > -1) {
                    isValidCellReference = false;
                    isFullColumn = isNullOrUndefined(ref.match(/[0-9]/)) ? true : false;
                }
                index = getSwapRange(getRangeIndexes(ref));
                updated = this.parent.updateRangeOnInsertDelete(args, index, isRangeFormula, row, col, isAbsoluteRef, isSingleRangeRef);
                if (updated) {
                    formulaArr[i] = index[2] < index[0] || index[3] < index[1] ?
                        this.calculateInstance.getErrorStrings()[CommonErrors.Ref] : !isValidCellReference ?
                        (isFullColumn ? getRangeAddress(index).replace(/\d/g, '') : getRangeAddress(index).replace(/[a-zA-Z]/g, '')) : getAddress();
                    refChanged = true;
                }
            }
        }
        let newFormula = '=' + formulaArr.join('');
        if (refChanged) {
            let regx;
            const regExp = RegExp;
            sheetInfo.forEach((info) => {
                if (newFormula.includes('`' + info.sheet + '`')) {
                    regx = new regExp('`' + info.sheet + '`', 'gi');
                    newFormula = newFormula.replace(regx, info.visibleName);
                }
            });
            if (cell.formula !== newFormula) {
                cell.formula = newFormula;
                const isManualInsertOrDelete = this.parent.calculationMode === 'Manual' && (args.isInsert || args.isDelete);
                const formulaStr = (cell.formula || '').toUpperCase();
                if (!isManualInsertOrDelete &&
                    !(((formulaStr.includes('UNIQUE') || formulaStr.includes('SORT')) && cell.value === '#SPILL!'))) {
                    cell.value = null;
                }
            }
        }
    }
    clearUniqueRange(row, col, sheet) {
        const uniqueArgs = { cellIdx: [row, col, row, col], isUnique: false, uniqueRange: '', sheetName: sheet.name };
        this.parent.notify(checkUniqueRange, uniqueArgs);
        const range = getRangeIndexes(uniqueArgs.uniqueRange);
        for (let i = range[0]; i <= range[2]; i++) {
            for (let j = range[1]; j <= range[3]; j++) {
                delete getCell(i, j, sheet, false, true).value;
            }
        }
    }
    clearSortRange(row, col, sheet) {
        const sortArgs = { cellIdx: [row, col, row, col], isSort: false, sortRange: '', sheetName: sheet.name };
        this.parent.notify(checkSortRange, sortArgs);
        const range = getRangeIndexes(sortArgs.sortRange);
        for (let i = range[0]; i <= range[2]; i++) {
            for (let j = range[1]; j <= range[3]; j++) {
                delete getCell(i, j, sheet, false, true).value;
            }
        }
    }
    clearAllUniqueFormulaValue() {
        const ranges = this.calculateInstance.uniqueRange;
        let cell;
        let sheet;
        let range;
        for (let i = 0; i < ranges.length; i++) {
            const lastIndex = ranges[i].lastIndexOf('!');
            sheet = getSheet(this.parent, getSheetIndex(this.parent, ranges[i].substring(0, lastIndex)));
            range = getRangeIndexes(ranges[i].substring(lastIndex + 1));
            cell = getCell(range[0], range[1], sheet);
            if (cell && cell.value === '#SPILL!') {
                continue;
            }
            for (let j = range[0]; j <= range[2]; j++) {
                for (let k = range[1]; k <= range[3]; k++) {
                    cell = getCell(j, k, sheet);
                    if (cell && cell.value) {
                        delete cell.value;
                    }
                }
            }
        }
    }
    clearAllSortFormulaValue() {
        const ranges = this.calculateInstance.sortRange;
        let cell;
        let sheet;
        let range;
        for (let i = 0; i < ranges.length; i++) {
            const lastIndex = ranges[i].lastIndexOf('!');
            sheet = getSheet(this.parent, getSheetIndex(this.parent, ranges[i].substring(0, lastIndex)));
            range = getRangeIndexes(ranges[i].substring(lastIndex + 1));
            cell = getCell(range[0], range[1], sheet);
            if (cell && cell.value === '#SPILL!') {
                continue;
            }
            for (let j = range[0]; j <= range[2]; j++) {
                for (let k = range[1]; k <= range[3]; k++) {
                    cell = getCell(j, k, sheet);
                    if (cell && cell.value) {
                        delete cell.value;
                    }
                }
            }
        }
    }
    parseFormulaArgument(args) {
        let temp;
        let str;
        let i = 0;
        const arr = [];
        let formulaVal = this.markSpecialChar(args.formula.replace('=', ''), args.rangeRef);
        const regExp = RegExp;
        const validCharRegx = new regExp(args.rangeRef ? /\(|\)|=|\^|>|<|\+|-|\*|\/|%|&/g : /\(|\)|=|\^|>|<|:|\+|-|\*|\/|%|&/g);
        const sepRegx = new regExp(this.parent.listSeparator, 'g');
        formulaVal = formulaVal.split(new regExp(validCharRegx.source + '|' + sepRegx.source, 'g'));
        const len = formulaVal.length;
        while (i < len) {
            temp = formulaVal[i];
            if (!temp) {
                i++;
                continue;
            }
            if (temp.length === 1) {
                arr.push(this.isUniqueChar(temp) ? this.getUniqueCharVal(temp) : temp);
            }
            else {
                str = temp[0];
                if (temp.indexOf('!') > 0) {
                    if (this.isUniqueChar(str)) {
                        arr.push(this.getUniqueCharVal(str));
                        temp = temp.substr(1);
                    }
                    str = temp.indexOf('!') + 1;
                    arr.push(temp.substring(0, str));
                    temp = temp.substring(str);
                    str = temp.indexOf(':');
                    if (str > -1 && temp.indexOf('!') > str) {
                        arr.push(temp.substring(0, str));
                        arr.push(':');
                        temp = temp.substring(str + 1);
                        str = temp.indexOf('!') + 1;
                        arr.push(temp.substring(0, str));
                        arr.push(temp.substring(str));
                    }
                    else {
                        arr.push(temp);
                    }
                }
                else if (this.isUniqueChar(str)) {
                    arr.push(this.getUniqueCharVal(str));
                    arr.push(temp.substr(1));
                }
                else {
                    arr.push(temp);
                }
            }
            i++;
        }
        args.formulaArr = arr;
        return arr;
    }
    getUniqueCharVal(formula) {
        switch (formula) {
            case this.uniqueOBracket:
                return '(';
            case this.uniqueCBracket:
                return ')';
            case this.uniqueCSeparator:
                return this.parent.listSeparator;
            case this.uniqueCOperator:
                return ':';
            case this.uniquePOperator:
                return '+';
            case this.uniqueSOperator:
                return '-';
            case this.uniqueMOperator:
                return '*';
            case this.uniqueDOperator:
                return '/';
            case this.uniqueModOperator:
                return '%';
            case this.uniqueConcateOperator:
                return '&';
            case this.uniqueEqualOperator:
                return '=';
            case this.uniqueExpOperator:
                return '^';
            case this.uniqueGTOperator:
                return '>';
            case this.uniqueLTOperator:
                return '<';
        }
        return '';
    }
    isUniqueChar(formula) {
        const code = formula.charCodeAt(formula);
        return code >= 129 && code <= 142;
    }
    markSpecialChar(formula, rangeRef) {
        formula = formula.replace(/\(/g, '(' + this.uniqueOBracket).replace(/\)/g, ')' + this.uniqueCBracket);
        const regEx = RegExp;
        if (rangeRef) {
            formula = formula.replace(new regEx(this.parent.listSeparator, 'g'), this.parent.listSeparator + this.uniqueCSeparator);
        }
        else {
            formula = formula.replace(new regEx(this.parent.listSeparator, 'g'), this.parent.listSeparator + this.uniqueCSeparator).replace(/:/g, `:${this.uniqueCOperator}`);
        }
        formula = formula.replace(/\+/g, '+' + this.uniquePOperator).replace(/-/g, '-' + this.uniqueSOperator);
        formula = formula.replace(/\*/g, '*' + this.uniqueMOperator).replace(/\//g, '/' + this.uniqueDOperator);
        formula = formula.replace(/&/g, '&' + this.uniqueConcateOperator);
        formula = formula.replace(/=/g, '=' + this.uniqueEqualOperator);
        formula = formula.replace(/\^/g, '^' + this.uniqueExpOperator);
        formula = formula.replace(/>/g, '>' + this.uniqueGTOperator).replace(/</g, '<' + this.uniqueLTOperator);
        return formula.replace(/%/g, '%' + this.uniqueModOperator);
    }
    refreshNamedRange(args) {
        if (args.definedNames && args.definedNames.length) {
            args.definedNames.forEach((definedName) => {
                this.parent.removeDefinedName(definedName.name, definedName.scope);
                this.parent.addDefinedName(definedName);
            });
            return;
        }
        const len = this.parent.definedNames.length;
        if (!len) {
            return;
        }
        const definedNames = Object.assign({}, this.parent.definedNames);
        let range;
        let sheetName;
        let refAddress;
        let definedName;
        let updated;
        let checkSheetName;
        let rangeAddress;
        const containAlphabetAndDigit = new RegExp(/^(?=.*[a-zA-Z])(?=.*\d)/g);
        let isValidCellReference;
        let isFullColumn;
        for (let i = 0; i < len; i++) {
            isValidCellReference = true;
            definedName = definedNames[i];
            const lastIndex = definedName.refersTo.lastIndexOf('!');
            refAddress = definedName.refersTo.substring(lastIndex + 1);
            sheetName = definedName.refersTo.substring(1, lastIndex);
            checkSheetName = sheetName;
            if (checkSheetName.match(/'/g)) {
                checkSheetName = checkSheetName.slice(1, -1);
            }
            if (checkSheetName !== args.sheet.name) {
                continue;
            }
            if (!containAlphabetAndDigit.test(refAddress) && refAddress.indexOf(':') > -1) {
                isValidCellReference = false;
                isFullColumn = isNullOrUndefined(refAddress.match(/[0-9]/)) ? true : false;
            }
            range = getRangeIndexes(refAddress);
            updated = this.parent.updateRangeOnInsertDelete(args, range);
            if (!isValidCellReference) {
                rangeAddress = isFullColumn ? getRangeAddress(range).replace(/\d/g, '') : getRangeAddress(range).replace(/[a-zA-Z]/g, '');
            }
            else {
                rangeAddress = getRangeAddress(range);
            }
            if (args.isInsert) {
                this.updateDefinedNames(definedName, sheetName, rangeAddress, updated);
            }
            else {
                if (args.modelType === 'Row') {
                    this.updateDefinedNames(definedName, sheetName, rangeAddress, updated, [range[0], range[2]], args);
                }
                else if (args.modelType === 'Column') {
                    this.updateDefinedNames(definedName, sheetName, rangeAddress, updated, [range[1], range[3]], args);
                }
            }
        }
    }
    updateDefinedNames(definedName, sheetName, rangeAddress, changed, idx, args) {
        if (!changed) {
            return;
        }
        const index = this.parent.definedNames.indexOf(definedName);
        const eventArgs = {
            action: 'removeDefinedName',
            isRemoved: false,
            definedName: definedName.name,
            scope: definedName.scope,
            isEventTrigger: true
        };
        this.parent.notify(workbookFormulaOperation, eventArgs);
        if (idx) {
            let oldDefinedName = { name: definedName.name, comment: definedName.comment, refersTo: definedName.refersTo,
                scope: definedName.scope };
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            oldDefinedName = new DefineName(this.parent, 'definedNames', oldDefinedName, true);
            if (args.definedNames) {
                args.definedNames.push(oldDefinedName);
            }
            else {
                args.definedNames = [oldDefinedName];
            }
            if (idx[1] < idx[0]) {
                return;
            }
        }
        definedName.refersTo = sheetName + '!' + rangeAddress;
        this.parent.notify(workbookFormulaOperation, { action: 'addDefinedName', definedName: definedName, isAdded: false, index: index, isEventTrigger: true });
        const refreshArgs = { name: definedName.name, scope: definedName.scope, comment: definedName.comment,
            refersTo: definedName.refersTo, cancel: false };
        this.parent.notify('actionComplete', { eventArgs: refreshArgs, action: 'refreshNamedRange' });
    }
}
